<template>
  <div :class="['matching-input', { 'is-disabled': disabled }]">
    <p class="matching-hint">从左侧圆点拖到右侧选项完成连线，也可以依次点击左右选项。</p>
    <div
      ref="canvas"
      class="matching-canvas"
      @pointermove="handlePointerMove"
      @pointerup="finishDrag"
      @pointercancel="cancelDrag"
    >
      <svg
        class="matching-lines"
        :width="canvasSize.width"
        :height="canvasSize.height"
        :viewBox="`0 0 ${canvasSize.width} ${canvasSize.height}`"
        aria-hidden="true"
      >
        <path
          v-for="line in lines"
          :key="line.leftKey"
          :d="line.path"
          :stroke="line.color"
          class="matching-line"
        />
        <path
          v-if="previewPath"
          :d="previewPath"
          :stroke="lineColor(activeLeftIndex)"
          class="matching-line is-preview"
        />
      </svg>

      <div class="matching-column matching-column--left">
        <div class="matching-column__title">题目项</div>
        <div
          v-for="option in leftOptions"
          :key="option.key"
          ref="leftNodes"
          class="matching-node matching-node--left"
          :class="{
            'is-active': activeLeft === String(option.key),
            'is-connected': connectionFor(option.key)
          }"
        >
          <button
            type="button"
            class="matching-node__select"
            :aria-pressed="activeLeft === String(option.key) ? 'true' : 'false'"
            :disabled="disabled"
            @click="selectLeft(option.key)"
          >
            <span class="matching-node__key">{{ option.key }}</span>
            <span class="matching-node__content" v-html="option.content"></span>
            <span v-if="connectionFor(option.key)" class="matching-node__status">
              → {{ connectionFor(option.key) }}
            </span>
          </button>
          <button
            v-if="connectionFor(option.key)"
            class="matching-clear"
            type="button"
            :aria-label="`清除 ${option.key} 的连线`"
            :disabled="disabled"
            @click="clearConnection(option.key)"
          >×</button>
          <span
            class="matching-port matching-port--start"
            aria-hidden="true"
            @click.stop
            @pointerdown.stop.prevent="startDrag(option.key, $event)"
          ></span>
        </div>
      </div>

      <div class="matching-column matching-column--right">
        <div class="matching-column__title">匹配项</div>
        <div
          v-for="option in rightOptions"
          :key="option.key"
          ref="rightNodes"
          class="matching-node matching-node--right"
          :class="{
            'is-target': Boolean(activeLeft),
            'is-connected': isRightConnected(option.key)
          }"
          role="button"
          :tabindex="disabled ? -1 : 0"
          :aria-pressed="isRightConnected(option.key) ? 'true' : 'false'"
          @click="connectSelected(option.key)"
          @keydown.enter.prevent="connectSelected(option.key)"
          @keydown.space.prevent="connectSelected(option.key)"
        >
          <span class="matching-port matching-port--end" aria-hidden="true"></span>
          <span class="matching-node__key">{{ option.key }}</span>
          <span class="matching-node__content" v-html="option.content"></span>
        </div>
      </div>
    </div>
    <p class="matching-progress">已完成 {{ completedCount }} / {{ leftOptions.length }} 条连线</p>
  </div>
</template>

<script>
const LINE_COLORS = ['#ff6b35', '#2196f3', '#4caf50', '#9c27b0', '#ff9800', '#009688', '#e91e63']

export default {
  name: 'MatchingQuestionInput',
  props: {
    leftOptions: {
      type: Array,
      default: () => []
    },
    rightOptions: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeLeft: '',
      activePointerId: null,
      dragging: false,
      preview: null,
      lines: [],
      canvasSize: { width: 1, height: 1 },
      resizeObserver: null
    }
  },
  computed: {
    completedCount() {
      return this.leftOptions.filter(option => Boolean(this.connectionFor(option.key))).length
    },
    previewPath() {
      if (!this.preview) return ''
      return this.createPath(this.preview.startX, this.preview.startY, this.preview.endX, this.preview.endY)
    },
    activeLeftIndex() {
      return this.leftOptions.findIndex(option => String(option.key) === this.activeLeft)
    }
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.scheduleLineUpdate()
      }
    },
    leftOptions: {
      deep: true,
      handler() {
        this.scheduleLineUpdate()
      }
    },
    rightOptions: {
      deep: true,
      handler() {
        this.scheduleLineUpdate()
      }
    }
  },
  mounted() {
    this.scheduleLineUpdate()
    window.addEventListener('resize', this.scheduleLineUpdate)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleLineUpdate)
      this.resizeObserver.observe(this.$refs.canvas)
    }
    this.$refs.canvas.addEventListener('load', this.scheduleLineUpdate, true)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleLineUpdate)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.$refs.canvas) this.$refs.canvas.removeEventListener('load', this.scheduleLineUpdate, true)
  },
  methods: {
    stringKey(value) {
      return String(value === null || typeof value === 'undefined' ? '' : value)
    },
    connectionFor(leftKey) {
      const pair = this.value.find(item => item && this.stringKey(item.left) === this.stringKey(leftKey))
      return pair && this.stringKey(pair.right) ? this.stringKey(pair.right) : ''
    },
    isRightConnected(rightKey) {
      return this.value.some(item => item && this.stringKey(item.right) === this.stringKey(rightKey))
    },
    lineColor(index) {
      return LINE_COLORS[(index < 0 ? 0 : index) % LINE_COLORS.length]
    },
    selectLeft(leftKey) {
      if (this.disabled) return
      const key = this.stringKey(leftKey)
      this.activeLeft = this.activeLeft === key ? '' : key
    },
    connectSelected(rightKey) {
      if (this.disabled) return
      if (!this.activeLeft) return
      this.setConnection(this.activeLeft, rightKey)
      this.activeLeft = ''
    },
    setConnection(leftKey, rightKey) {
      if (this.disabled) return
      const left = this.stringKey(leftKey)
      const right = this.stringKey(rightKey)
      const current = new Map(this.value.map(item => [this.stringKey(item && item.left), this.stringKey(item && item.right)]))
      current.set(left, right)
      const nextValue = this.leftOptions.map(option => ({
        left: this.stringKey(option.key),
        right: current.get(this.stringKey(option.key)) || ''
      }))
      this.$emit('input', nextValue)
      this.$nextTick(this.updateLines)
    },
    clearConnection(leftKey) {
      this.setConnection(leftKey, '')
      if (this.activeLeft === this.stringKey(leftKey)) this.activeLeft = ''
    },
    startDrag(leftKey, event) {
      if (this.disabled) return
      const canvas = this.$refs.canvas
      if (!canvas) return
      this.activeLeft = this.stringKey(leftKey)
      this.dragging = true
      this.activePointerId = event.pointerId
      this.updateLines()
      const start = this.leftAnchor(this.activeLeft)
      const point = this.localPoint(event)
      this.preview = start ? {
        startX: start.x,
        startY: start.y,
        endX: point.x,
        endY: point.y
      } : null
      if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId)
    },
    handlePointerMove(event) {
      if (!this.dragging || event.pointerId !== this.activePointerId || !this.preview) return
      const point = this.localPoint(event)
      this.preview = { ...this.preview, endX: point.x, endY: point.y }
    },
    finishDrag(event) {
      if (!this.dragging || event.pointerId !== this.activePointerId) return
      const rightKey = this.rightAtPoint(event.clientX, event.clientY)
      if (rightKey) {
        this.setConnection(this.activeLeft, rightKey)
        this.activeLeft = ''
      }
      this.releasePointer(event.pointerId)
    },
    cancelDrag(event) {
      if (!this.dragging || event.pointerId !== this.activePointerId) return
      this.releasePointer(event.pointerId)
    },
    releasePointer(pointerId) {
      const canvas = this.$refs.canvas
      if (canvas && canvas.releasePointerCapture && canvas.hasPointerCapture && canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId)
      }
      this.dragging = false
      this.activePointerId = null
      this.preview = null
    },
    rightAtPoint(clientX, clientY) {
      const nodes = this.refList('rightNodes')
      const index = nodes.findIndex(node => {
        const rect = node.getBoundingClientRect()
        return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
      })
      return index >= 0 && this.rightOptions[index] ? this.stringKey(this.rightOptions[index].key) : ''
    },
    localPoint(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return {
        x: Math.max(0, Math.min(rect.width, event.clientX - rect.left)),
        y: Math.max(0, Math.min(rect.height, event.clientY - rect.top))
      }
    },
    leftAnchor(leftKey) {
      const index = this.leftOptions.findIndex(option => this.stringKey(option.key) === this.stringKey(leftKey))
      const node = this.refList('leftNodes')[index]
      const canvas = this.$refs.canvas
      if (!node || !canvas) return null
      const nodeRect = node.getBoundingClientRect()
      const canvasRect = canvas.getBoundingClientRect()
      return {
        x: nodeRect.right - canvasRect.left,
        y: nodeRect.top + nodeRect.height / 2 - canvasRect.top
      }
    },
    rightAnchor(rightKey) {
      const index = this.rightOptions.findIndex(option => this.stringKey(option.key) === this.stringKey(rightKey))
      const node = this.refList('rightNodes')[index]
      const canvas = this.$refs.canvas
      if (!node || !canvas) return null
      const nodeRect = node.getBoundingClientRect()
      const canvasRect = canvas.getBoundingClientRect()
      return {
        x: nodeRect.left - canvasRect.left,
        y: nodeRect.top + nodeRect.height / 2 - canvasRect.top
      }
    },
    refList(name) {
      const refs = this.$refs[name]
      if (!refs) return []
      return Array.isArray(refs) ? refs : [refs]
    },
    createPath(startX, startY, endX, endY) {
      const curve = Math.max(24, Math.abs(endX - startX) * 0.42)
      return `M ${startX} ${startY} C ${startX + curve} ${startY}, ${endX - curve} ${endY}, ${endX} ${endY}`
    },
    scheduleLineUpdate() {
      this.$nextTick(this.updateLines)
    },
    updateLines() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      this.canvasSize = {
        width: Math.max(1, canvas.clientWidth),
        height: Math.max(1, canvas.clientHeight)
      }
      this.lines = this.leftOptions.map((option, index) => {
        const rightKey = this.connectionFor(option.key)
        if (!rightKey) return null
        const start = this.leftAnchor(option.key)
        const end = this.rightAnchor(rightKey)
        if (!start || !end) return null
        return {
          leftKey: this.stringKey(option.key),
          color: this.lineColor(index),
          path: this.createPath(start.x, start.y, end.x, end.y)
        }
      }).filter(Boolean)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.matching-input {
  display: grid;
  gap: 10px;
}

.matching-input.is-disabled {
  opacity: 0.65;
}

.matching-input.is-disabled .matching-node--right,
.matching-input.is-disabled .matching-port {
  cursor: not-allowed;
  pointer-events: none;
}

.matching-hint,
.matching-progress {
  margin: 0;
  color: $color-text-secondary;
  font-size: 13px;
  line-height: 1.6;
}

.matching-progress {
  text-align: right;
  font-weight: 700;
}

.matching-canvas {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(48px, 10vw, 120px);
  padding: 14px;
  overflow: hidden;
  border: 1px solid $color-border-light;
  border-radius: var(--radius-md);
  background: linear-gradient(90deg, rgba($color-primary, 0.025), rgba($color-info, 0.025));
}

.matching-lines {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: visible;
  pointer-events: none;
}

.matching-line {
  fill: none;
  stroke-linecap: round;
  stroke-width: 3;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.12));
}

.matching-line.is-preview {
  opacity: 0.65;
  stroke-dasharray: 7 5;
}

.matching-column {
  position: relative;
  z-index: 2;
  display: grid;
  align-content: start;
  gap: 12px;
}

.matching-column__title {
  color: $color-text-secondary;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-align: center;
}

.matching-node {
  position: relative;
  display: flex;
  min-height: 54px;
  align-items: center;
  gap: 9px;
  padding: 10px 13px;
  border: 1px solid $color-border;
  border-radius: var(--radius-md);
  background: $color-surface;
  color: $color-text-primary;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  user-select: none;
}

.matching-node:hover,
.matching-node:focus-within,
.matching-node.is-active {
  border-color: $color-primary;
  box-shadow: 0 0 0 3px rgba($color-primary, 0.12);
}

.matching-node.is-active {
  transform: translateY(-1px);
}

.matching-node--right.is-target {
  border-style: dashed;
}

.matching-node--right.is-target:hover,
.matching-node--right.is-target:focus-visible {
  border-style: solid;
  border-color: $color-info;
  box-shadow: 0 0 0 3px rgba($color-info, 0.12);
}

.matching-node__select {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 9px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.matching-node__status {
  flex: 0 0 auto;
  color: $color-success;
  font-size: 12px;
  font-weight: 800;
}

.matching-node__key {
  display: inline-flex;
  width: 27px;
  height: 27px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $color-background;
  color: $color-text-secondary;
  font-size: 12px;
  font-weight: 800;
}

.matching-node__content {
  min-width: 0;
  flex: 1;
  line-height: 1.55;
  word-break: break-word;
}

.matching-port {
  position: absolute;
  top: 50%;
  z-index: 3;
  width: 18px;
  height: 18px;
  border: 4px solid $color-surface;
  border-radius: 50%;
  background: $color-primary;
  box-shadow: 0 0 0 1px rgba($color-primary, 0.35);
  transform: translateY(-50%);
}

.matching-port--start {
  right: -10px;
  cursor: grab;
  touch-action: none;
}

.matching-port--start:active {
  cursor: grabbing;
}

.matching-port--end {
  left: -10px;
  background: $color-info;
  box-shadow: 0 0 0 1px rgba($color-info, 0.35);
}

.matching-clear {
  display: inline-flex;
  width: 25px;
  height: 25px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba($color-danger, 0.1);
  color: $color-danger;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
}

@media (max-width: 600px) {
  .matching-canvas {
    gap: 42px;
    padding: 10px 8px;
  }

  .matching-node {
    min-height: 50px;
    gap: 6px;
    padding: 8px;
    font-size: 13px;
  }

  .matching-node__key {
    width: 23px;
    height: 23px;
  }

  .matching-clear {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 21px;
    height: 21px;
  }
}
</style>
